// Approval chains (D3, ruling Q12) — the template editor's "Approval chain"
// section for Special-form and E-sign templates: ordered stages (role,
// caption + statement, co-signer caption / statement on the Buyer stage,
// Mandatory), reorder, remove, add, and the Borrower-facing toggle.
//
// Editable only for holders of Collection.ManageApprovalChains; everyone else
// sees it read-only. The client mirror (approvalChainRules) refuses what the
// server would: a stage mandatory in the SAVED version (`baseline`) cannot be
// removed, moved or made optional; a role appears at most once; a
// borrower-facing chain keeps Licensed Intermediary then Note Holder (both
// mandatory) ahead of any Borrower stage. The server re-validates every save.

import { ArrowDown, ArrowUp, Lock, Plus, Trash2 } from "lucide-react";
import { Badge, Button, FormField, Input, Textarea, Tooltip } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import type {
  ApprovalChainDefinition,
  ApprovalChainRole,
  ApprovalChainStageDefinition,
} from "@/api/approvalChains";
import {
  COUNSEL_PLACEHOLDER,
  COUNSEL_PLACEHOLDER_WARNING,
  D2_ROLES,
  MANDATORY_LOCK_HINT,
  MAX_STAGES,
  canMoveStage,
  canRemoveStage,
  isLockedAt,
  moveStage,
  roleLabel,
  stageHasPlaceholder,
  unusedRoles,
} from "./approvalChainRules";

export interface ApprovalChainEditorProps {
  /** The chain being edited; null = the template carries no chain. */
  value: ApprovalChainDefinition | null;
  onChange: (next: ApprovalChainDefinition | null) => void;
  isBorrowerFacing: boolean;
  onBorrowerFacingChange: (next: boolean) => void;
  /** The SAVED version's chain — its mandatory stages are locked. Null for a
   *  new template or one that had no chain. */
  baseline: ApprovalChainDefinition | null;
  readOnly: boolean;
  /** Client-mirror validation messages, shown under the stages. */
  errors: string[];
}

/** The placeholder chip shown on a stage still carrying counsel's placeholder. */
export function CounselPlaceholderChip() {
  return (
    <Badge status="review" data-testid="counsel-placeholder-chip">
      {COUNSEL_PLACEHOLDER} · {COUNSEL_PLACEHOLDER_WARNING}
    </Badge>
  );
}

/** The lock shown on a mandatory stage, with its hint on hover and focus. */
export function MandatoryLock() {
  return (
    <Tooltip label={MANDATORY_LOCK_HINT}>
      <span
        tabIndex={0}
        aria-label={MANDATORY_LOCK_HINT}
        className="inline-flex items-center text-gray-mid"
        data-testid="mandatory-lock"
      >
        <Lock size={14} aria-hidden />
      </span>
    </Tooltip>
  );
}

export function ApprovalChainEditor({
  value,
  onChange,
  isBorrowerFacing,
  onBorrowerFacingChange,
  baseline,
  readOnly,
  errors,
}: ApprovalChainEditorProps) {
  const stages = value?.stages ?? [];
  const baselineHasMandatory = baseline?.stages.some((s) => s.mandatory) ?? false;
  const addable = unusedRoles(stages);

  const setStages = (next: ApprovalChainStageDefinition[]) => onChange({ stages: next });
  const patchStage = (index: number, patch: Partial<ApprovalChainStageDefinition>) =>
    setStages(stages.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const addStage = () => {
    const role = addable[0];
    if (!role) return;
    setStages([
      ...stages,
      {
        role,
        mandatory: false,
        caption: "",
        statement: "",
        ...(role === "Buyer" ? { coSignerCaption: "", coSignerStatement: "" } : {}),
      },
    ]);
  };

  return (
    <div className="flex flex-col gap-space-3" data-testid="approval-chain-editor">
      {readOnly && (
        <p className="text-small text-gray-mid m-0" data-testid="approval-chain-read-only">
          Changing the approval chain requires the approval-chain admin permission.
        </p>
      )}

      <label className="flex items-start gap-space-2 text-small text-onyx">
        <input
          type="checkbox"
          className="mt-space-1"
          checked={isBorrowerFacing}
          disabled={readOnly}
          onChange={(e) => onBorrowerFacingChange(e.target.checked)}
          data-testid="borrower-facing-toggle"
        />
        <span className="flex flex-col">
          <span>Borrower-facing</span>
          <span className="text-caption text-gray-mid">
            The Licensed Intermediary and then the Note Holder must both act (mandatory) before
            the Borrower receives it, and each send asks staff for the two engagement
            attestations.
          </span>
        </span>
      </label>

      {value === null ? (
        <div className="flex flex-col gap-space-2">
          <p className="text-small text-gray-mid m-0">This template has no approval chain.</p>
          {!readOnly && (
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onChange({ stages: [] })}
                data-testid="approval-chain-add"
              >
                <Plus size={14} aria-hidden /> Add an approval chain
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <ol className="m-0 p-0 list-none flex flex-col gap-space-3">
            {stages.map((stage, index) => {
              const locked = isLockedAt(baseline, index);
              const otherRoles = new Set(stages.filter((_, i) => i !== index).map((s) => s.role));
              return (
                <li
                  key={`${index}-${stage.role}`}
                  className="flex flex-col gap-space-2 rounded-md border border-border-light bg-white-pure p-space-3"
                  data-testid={`chain-stage-${index}`}
                >
                  <div className="flex flex-wrap items-center gap-space-2">
                    <span className="text-small font-semibold text-onyx">Stage {index + 1}</span>
                    {locked && <MandatoryLock />}
                    {stageHasPlaceholder(stage, true) && <CounselPlaceholderChip />}
                    <span className="flex-1" />
                    {!readOnly && (
                      <>
                        <Button
                          type="button"
                          variant="icon"
                          size="icon"
                          aria-label={`Move stage ${index + 1} up`}
                          disabled={!canMoveStage(stages, index, -1, baseline)}
                          onClick={() => setStages(moveStage(stages, index, -1))}
                        >
                          <ArrowUp size={14} aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="icon"
                          size="icon"
                          aria-label={`Move stage ${index + 1} down`}
                          disabled={!canMoveStage(stages, index, 1, baseline)}
                          onClick={() => setStages(moveStage(stages, index, 1))}
                        >
                          <ArrowDown size={14} aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="icon"
                          size="icon"
                          aria-label={`Remove stage ${index + 1}`}
                          disabled={!canRemoveStage(stages, index, baseline)}
                          onClick={() => setStages(stages.filter((_, i) => i !== index))}
                        >
                          <Trash2 size={14} aria-hidden />
                        </Button>
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap items-end gap-space-3">
                    <FormField label="Role">
                      <Select
                        value={stage.role}
                        disabled={readOnly || locked}
                        aria-label={`Stage ${index + 1} role`}
                        onChange={(e) => {
                          const role = e.target.value as ApprovalChainRole;
                          patchStage(index, {
                            role,
                            ...(role === "Buyer"
                              ? {
                                  coSignerCaption: stage.coSignerCaption ?? "",
                                  coSignerStatement: stage.coSignerStatement ?? "",
                                }
                              : { coSignerCaption: null, coSignerStatement: null }),
                          });
                        }}
                      >
                        {D2_ROLES.map((r) => (
                          <option key={r} value={r} disabled={otherRoles.has(r)}>
                            {roleLabel(r)}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                    <label className="flex items-center gap-space-2 text-small text-onyx pb-space-2">
                      <input
                        type="checkbox"
                        checked={stage.mandatory}
                        disabled={readOnly || locked}
                        aria-label={`Stage ${index + 1} mandatory`}
                        onChange={(e) => patchStage(index, { mandatory: e.target.checked })}
                      />
                      Mandatory
                    </label>
                  </div>

                  <FormField label="Caption">
                    <Input
                      value={stage.caption}
                      readOnly={readOnly}
                      aria-label={`Stage ${index + 1} caption`}
                      onChange={(e) => patchStage(index, { caption: e.target.value })}
                    />
                  </FormField>
                  <FormField label="Statement">
                    <Textarea
                      rows={2}
                      value={stage.statement}
                      readOnly={readOnly}
                      aria-label={`Stage ${index + 1} statement`}
                      onChange={(e) => patchStage(index, { statement: e.target.value })}
                    />
                  </FormField>
                  {stage.role === "Buyer" && (
                    <>
                      <FormField
                        label="Co-signer caption"
                        helperText="Captions each co-borrower / guarantor block added on a send."
                      >
                        <Input
                          value={stage.coSignerCaption ?? ""}
                          readOnly={readOnly}
                          aria-label={`Stage ${index + 1} co-signer caption`}
                          onChange={(e) => patchStage(index, { coSignerCaption: e.target.value })}
                        />
                      </FormField>
                      <FormField label="Co-signer statement">
                        <Textarea
                          rows={2}
                          value={stage.coSignerStatement ?? ""}
                          readOnly={readOnly}
                          aria-label={`Stage ${index + 1} co-signer statement`}
                          onChange={(e) => patchStage(index, { coSignerStatement: e.target.value })}
                        />
                      </FormField>
                    </>
                  )}
                </li>
              );
            })}
          </ol>

          {!readOnly && (
            <div className="flex flex-wrap items-center gap-space-2">
              <Button
                type="button"
                variant="secondary"
                onClick={addStage}
                disabled={addable.length === 0 || stages.length >= MAX_STAGES}
                data-testid="approval-chain-add-stage"
              >
                <Plus size={14} aria-hidden /> Add stage
              </Button>
              <Button
                type="button"
                variant="tertiary"
                onClick={() => onChange(null)}
                disabled={baselineHasMandatory}
                data-testid="approval-chain-remove"
              >
                Remove approval chain
              </Button>
              {baselineHasMandatory && (
                <span className="text-caption text-gray-mid">
                  The saved chain has mandatory stages, so it cannot be removed.
                </span>
              )}
            </div>
          )}
        </>
      )}

      {errors.length > 0 && (
        <ul
          className="m-0 rounded-md bg-status-error/10 p-space-3 pl-space-6 text-small text-onyx"
          data-testid="approval-chain-errors"
        >
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
